import { useRef, useState } from 'react';

import { Box, TextInput } from '@opendoor/bricks-next';
import type { TextInputSize } from 'platform/components/AccountPreferences';
import { genericErrorText } from 'platform/components/AccountPreferences';
import type { CustomerDetailsQuery_customerService_OdProtosCustomerV1Services_Customer_Query_getCurrentCustomer_OdProtosCustomerV1Services_CustomerResponses_GetCurrentCustomer_customer_OdProtosCustomerV1Data_Customer as Customer } from 'platform/graphql/__generated__/useCustomerDetails';
import { useUpdateCustomerDetails } from 'platform/graphql/useCustomerDetails';
import type { ShowToastFn } from 'shared/hooks/useToast';

import { SectionHeader } from './SectionHeader';

/**
 * Displays first name and last name and allows user to update the same.
 *
 */
export const BasicInfoContainer = ({
  customerInfo,
  inputSize,
  showToast,
}: {
  customerInfo?: Customer | null;
  inputSize: TextInputSize;
  showToast: ShowToastFn;
}) => {
  const savedFormState = useRef<{
    firstName?: string;
    lastName?: string;
  }>({
    firstName: customerInfo?.firstName,
    lastName: customerInfo?.lastName,
  });

  // Customer Info
  const [firstName, setFirstName] = useState<string>(customerInfo?.firstName || '');
  const [firstNameError, setFirstNameError] = useState<string>('');

  const [lastName, setLastName] = useState<string>(customerInfo?.lastName || '');
  const [lastNameError, setLastNameError] = useState<string>('');

  const { updateCustomerDetails, loading: updatingCustomerDetails } = useUpdateCustomerDetails();

  const isFormValid = () => {
    let valid = true;
    if (firstName.length < 1) {
      setFirstNameError('This field cannot be empty');
      valid = false;
    }

    if (lastName.length < 1) {
      setLastNameError('This field cannot be empty');
      valid = false;
    }

    return valid;
  };

  const updateBasicInfo = async () => {
    if (!isFormValid()) return;

    //No dirty fields
    if (
      firstName === savedFormState.current.firstName &&
      lastName === savedFormState.current.lastName
    ) {
      return;
    }

    try {
      const { data, error } = await updateCustomerDetails({
        variables: {
          firstName,
          lastName,
        },
      });

      if (error) {
        setFirstNameError(genericErrorText);
        return;
      }

      // Update current form state for dirty check and passing old phone number
      const updatedCustomer = data?.customerService?.updateCurrent?.updatedCustomer;
      const formState = {
        firstName: updatedCustomer?.firstName || '',
        lastName: updatedCustomer?.lastName || '',
      };
      savedFormState.current = formState;
      showToast({
        type: 'success',
        message: 'Your first name and last name have been updated',
        analyticsName: 'cake-basic-info-success-toast',
      });
    } catch {
      setFirstNameError(genericErrorText);
    }
  };

  return (
    <Box
      testID="info-container"
      gap="$12x"
      $largerThanSM={{
        gap: '$16x',
      }}
    >
      <SectionHeader
        header="Your info"
        saveButtonId="save-basic-info"
        analyticsName="save-first-last-name"
        onSave={() => void updateBasicInfo()}
        inputSize={inputSize}
        updating={updatingCustomerDetails}
      />
      <Box
        $largerThanSM={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: '$44x',
        }}
        gap="$12x"
      >
        <Box flex={1}>
          <TextInput
            id="first-name-field"
            size={inputSize}
            title="First Name"
            value={firstName}
            onChangeText={(value) => {
              setFirstName(value);
              setFirstNameError('');
            }}
            errorText={firstNameError}
            required
          />
        </Box>
        <Box flex={1}>
          <TextInput
            id="last-name-field"
            size={inputSize}
            title="Last Name"
            value={lastName}
            onChangeText={(value) => {
              setLastName(value);
              setLastNameError('');
            }}
            errorText={lastNameError}
            required
          />
        </Box>
      </Box>
    </Box>
  );
};
